import type { AppDefinition, ModulesOuverts, SurfacesPersonnellesOuvertes } from '../types';

/** Une tuile n'est cliquable que si l'URL de production a ete verifiee. */
export function isAppOpenable(app: AppDefinition): boolean {
  return Boolean(app.url?.trim());
}

/**
 * Les applications DECLAREES EN SERVICE qui n'ont pas d'adresse — la liste doit
 * rester vide.
 *
 * Defaut mesure le 2026-09-15 : `GS-ZoneSecur` et `UF WebEditor` portaient
 * `status: 'prod'` avec une adresse vide. Le lanceur affirmait un etat que rien
 * ne soutenait, et leurs tuiles etaient grises — les deux repondent pourtant
 * (200 en 0,24 s et 0,30 s). Un etat declare qu'aucune adresse ne soutient est
 * une affirmation sans preuve : ou l'application a une adresse et on la donne,
 * ou elle n'en a pas et elle n'est pas en service.
 *
 * Rendue en FONCTION, et pas ecrite dans le test, pour que le temoin puisse
 * prouver qu'il mord : le test lui donne une application fabriquee « en service
 * sans adresse » et verifie qu'elle est attrapee.
 */
export function declaredInServiceWithoutAddress(apps: AppDefinition[]): string[] {
  return apps.filter((a) => a.status === 'prod' && !isAppOpenable(a)).map((a) => a.id);
}

/**
 * Motif du grisage, ou `null` si la tuile s'ouvre.
 *
 * POURQUOI CETTE FONCTION EXISTE — mesure du 2026-09-15 : sept tuiles sur
 * quinze etaient grisees et AUCUNE ne disait pourquoi. Quelqu'un qui decouvre
 * le parc lit du gris sans motif comme « c'est casse ».
 *
 * Le motif est DERIVE, jamais saisi a la main. Il n'y en a qu'un parce qu'il ne
 * peut y en avoir qu'un : le temoin `toute application declaree en service
 * porte son adresse` (apps.test.ts) interdit qu'une tuile grise soit autre
 * chose qu'une application pas encore livree. Si ce temoin rougit, ce motif
 * ment — les deux se lisent ensemble.
 */
export function unavailableReasonKey(app: AppDefinition): string | null {
  return isAppOpenable(app) ? null : 'hub:cards.notYetOpenable';
}

/** URL ouverte par la tuile, avec `gs_hint` pour le SSO silencieux. */
export function buildAppOpenUrl(baseUrl: string, userEmail?: string): string {
  const email = userEmail?.trim();
  if (!email) return baseUrl;
  const sep = baseUrl.includes('?') ? '&' : '?';
  return `${baseUrl}${sep}gs_hint=${encodeURIComponent(email)}`;
}

/**
 * NOM DE FENETRE d'une application — une application, UNE fenetre.
 *
 * DEFAUT REPARE : chaque clic sur une tuile ouvrait une fenetre de PLUS. Trois
 * clics sur la meme application donnaient trois fenetres de la meme application
 * (`window.open(url, '_blank', 'noopener')`).
 *
 * Le nom est DERIVE de l'identifiant du catalogue, jamais saisi : deux
 * applications ne peuvent donc pas partager un nom par inattention — ce serait
 * le defaut INVERSE, ou ouvrir `b` ecraserait `a` dans la meme fenetre (mesure
 * ci-dessous, contre-epreuve).
 *
 * MESURE EN CHROME REEL, 2026-09-15, 3 clics sur la meme application :
 *
 * | ce qu'on passe a `window.open`            | fenetres |
 * |-------------------------------------------|----------|
 * | `'_blank'` + `noopener` (ce qu'on faisait) |     3    |
 * | nom par app + `noopener`                   |     3    |
 * | nom par app, SANS `noopener`               |     1    |
 * | `'_blank'`, SANS `noopener`                |     3    |
 *
 * Second temoin, obligatoire : deux applications DIFFERENTES -> 2 fenetres.
 * Contre-epreuve : un nom unique partage par tout le parc -> 1 fenetre, la
 * seconde application ecrase la premiere.
 *
 * POURQUOI `noopener` DISPARAIT, et ce que ca coute. Avec `noopener`, le
 * navigateur applique bien le nom a la fenetre creee, mais ne CHERCHE pas une
 * fenetre existante qui le porte : il en ouvre une neuve a chaque fois. La
 * contrepartie evidente — rouvrir sans `noopener` puis neutraliser le lien
 * retour par `poignee.opener = null` — a ete mesuree et NE MARCHE PAS : elle
 * fait retomber a 3 fenetres sur 3 clics (couper le lien retour sort la fenetre
 * du groupe ou le nom se cherche). Il n'existe donc pas de contrepartie qui
 * garde les deux. Ce qui reste expose : une page ouverte peut faire naviguer
 * l'onglet du lanceur. Toutes les destinations du catalogue sont des
 * applications GS servies par nous ; elles restent cross-origine, donc elles ne
 * peuvent RIEN LIRE du lanceur. Le referent etait deja transmis avant ce
 * changement (`noopener` n'est pas `noreferrer`) : il n'y a pas de fuite neuve.
 */
export function appWindowName(appId: string): string {
  return `gs-app-${appId}`;
}

/**
 * Ouvre une application du catalogue — geste UNIQUE, partage par la tuile du
 * catalogue et par la tuile epinglee.
 *
 * Il est ici, et pas recopie dans les deux composants, parce que les deux
 * ouvrent LA MEME application : une tuile epinglee et sa tuile de catalogue qui
 * divergeraient sur le nom de fenetre rouvriraient une seconde fenetre pour
 * cette application — exactement le defaut repare.
 *
 * `ouvrir` est injectable pour que le temoin puisse lire CE QU'ON PASSE au
 * navigateur. Ce que le temoin prouve : deux clics sur la meme application
 * donnent le meme nom de fenetre, deux applications differentes donnent deux
 * noms, et `noopener` n'est plus passe. Ce qu'il ne prouve PAS, parce qu'aucun
 * test hors navigateur ne le peut : que le navigateur ramene bien la fenetre au
 * premier plan — cela vient de la mesure en Chrome reel citee dans
 * `appWindowName`.
 */
export function openAppWindow(
  app: AppDefinition,
  userEmail?: string,
  ouvrir: (url: string, nom: string) => unknown = (url, nom) => window.open(url, nom),
): boolean {
  if (!isAppOpenable(app) || !app.url) return false;
  ouvrir(buildAppOpenUrl(app.url.trim(), userEmail), appWindowName(app.id));
  return true;
}

/** Zone 1 — metier GS, visible a tout employe authentifie (hors fun, hors adminOnly). */
export function gsZoneApps(apps: AppDefinition[]): AppDefinition[] {
  return apps.filter((a) => !a.adminOnly && a.zone !== 'fun' && a.zone !== 'espace-gs');
}

/** Zone 2 — Administration. Le page ne la rend que si `isAdmin`. */
export function adminZoneApps(apps: AppDefinition[]): AppDefinition[] {
  return apps.filter((a) => a.adminOnly);
}

/** Zone 3 — Espace GS. */
export function funZoneApps(apps: AppDefinition[]): AppDefinition[] {
  return apps.filter((a) => a.zone === 'fun' || a.zone === 'espace-gs');
}

/**
 * Ce que voit un non-admin sur /apps : zone GS + Espace GS.
 * `adminOnly` n'est PAS rendu — mesuree dans AppsPage (`isAdmin && adminApps`).
 */
export function visibleToNonAdmin(apps: AppDefinition[]): AppDefinition[] {
  return apps.filter((a) => !a.adminOnly);
}

/**
 * LA PORTE SUR LES TUILES (ADR-089 regle 3) — une tuile de module n'est montree que si les
 * marches 1 a 3 sont vraies pour ce module : le serveur les lit (/api/me `modules`), le lanceur
 * ne fait qu'afficher. CHOISIT UNE TUILE, N'AUTORISE RIEN : chaque application garde sa porte.
 *
 * - `module: null` (tuile sans module, declaree) : montree.
 * - module declare : montree seulement s'il figure dans les modules OUVERTS. Lecture ratee ou
 *   absente (`mesure: false`, serveur plus ancien) : cachee — une marche qui ne sait pas
 *   repondre refuse.
 * - module NON declare (champ absent) : montree, le temps que le registre des applications
 *   donne sa valeur ; `tuilesSansModuleDeclare` les nomme pour que ce passage se voie.
 */
export function tuileOuverteParLaPorte(
  app: AppDefinition,
  modules: ModulesOuverts | undefined,
  surfaces?: SurfacesPersonnellesOuvertes,
): boolean {
  if (app.module === undefined || app.module === null) return true;
  // LA SURFACE PERSONNELLE (2026-10-22) : la tuile d un module s ouvre aussi a une personne reconnue
  // sans role, pour son seul portail ; lecture ratee ou absente = aucune tuile par ce chemin.
  if (surfaces?.mesure && surfaces.ouvertes.includes(app.module)) return true;
  if (!modules || !modules.mesure) return false;
  return modules.ouverts.includes(app.module);
}

/** Les tuiles que la porte laisse voir, dans l'ordre du catalogue. */
export function tuilesSelonLaPorte(
  apps: AppDefinition[],
  modules: ModulesOuverts | undefined,
  surfaces?: SurfacesPersonnellesOuvertes,
): AppDefinition[] {
  return apps.filter((a) => tuileOuverteParLaPorte(a, modules, surfaces));
}

/** Les tuiles dont le module n'est pas encore declare (ni un module, ni `null`). */
export function tuilesSansModuleDeclare(apps: AppDefinition[]): string[] {
  return apps.filter((a) => a.module === undefined).map((a) => a.id);
}
